import { Methods } from '../../api/methods.js';
import type { ParsedArgs } from '../args.js';
import { flag, hasFlag } from '../args.js';
import { CliError, ExitCode } from '../errors.js';
import type { CommandResult } from '../output.js';
import { withClient } from './common.js';

export async function systemsCommand(args: ParsedArgs): Promise<CommandResult> {
  const action = args.positionals[1] ?? 'list';
  if (action === 'list') {
    const data = await withClient(args.options, (client) =>
      client.request(Methods.Systems, hasFlag(args.flags, 'all') ? { all: true } : undefined),
    );
    return {
      data: selectSystems(
        data,
        flag(args.flags, 'filter'),
        flag(args.flags, 'category'),
        hasFlag(args.flags, 'summary'),
      ),
    };
  }
  throw new CliError(`Unknown systems action "${action}"`, ExitCode.Usage);
}

function selectSystems(
  value: unknown,
  filter: string | undefined,
  category: string | undefined,
  summary: boolean,
): unknown {
  if (!filter && !category && !summary) return value;
  const wrapper =
    !Array.isArray(value) && value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : undefined;
  const systems = Array.isArray(value)
    ? value
    : wrapper && Array.isArray(wrapper.systems)
      ? (wrapper.systems as unknown[])
      : undefined;
  if (!systems) return value;
  const query = filter?.toLowerCase();
  const categoryQuery = category?.toLowerCase();
  const selected = systems.filter((system) => {
    if (!system || typeof system !== 'object') return false;
    const record = system as Record<string, unknown>;
    const systemCategory = typeof record.category === 'string' ? record.category : '';
    if (categoryQuery && systemCategory.toLowerCase() !== categoryQuery) return false;
    if (!query) return true;
    return [record.id, record.name, record.category, record.manufacturer]
      .filter((entry): entry is string => typeof entry === 'string')
      .some((entry) => entry.toLowerCase().includes(query));
  });
  if (!summary) return wrapper ? { ...wrapper, systems: selected } : selected;
  const categories: Record<string, number> = {};
  for (const system of selected) {
    const record = system as Record<string, unknown>;
    const name =
      typeof record.category === 'string' && record.category ? record.category : 'uncategorized';
    categories[name] = (categories[name] ?? 0) + 1;
  }
  return { total: selected.length, categories };
}

export async function launchersCommand(args: ParsedArgs): Promise<CommandResult> {
  const action = args.positionals[1] ?? 'list';
  const method =
    action === 'list'
      ? Methods.Launchers
      : action === 'refresh'
        ? Methods.LaunchersRefresh
        : undefined;
  if (!method) throw new CliError(`Unknown launchers action "${action}"`, ExitCode.Usage);
  return { data: await withClient(args.options, (client) => client.request(method)) };
}
